// Strips credentials and personal data out of Datadog RUM and Logs events
// before they leave the browser. Ported from hogc-one-mobile's
// src/utils/log-scrubber.js, with the web profile's own field names.
//
// Two leaks this closes, both seen in prod on 2026-09-30:
// - RUM kept every URL verbatim, and prefilled form iframes carry the member's
//   name, phone, personal id, partial NRIC and date of birth in the query.
// - Logs had no scrubber at all, so an error that stringified an Axios config
//   shipped its Authorization header.

// A JWT: three base64url segments, the first starting `eyJ`.
const JWT = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g

// Any Authorization header value, whether or not it holds a JWT.
const AUTH_HEADER = /("?[Aa]uthorization"?\s*[:=]\s*"?)(Bearer\s+)?[A-Za-z0-9._\-~+/]+=*/g

// JSON key/value pairs whose value is personal data or a secret. The key stays
// so a log still shows the field was there.
const SENSITIVE_PAIR = new RegExp(
  '"(' +
    [
      // credentials
      'token',
      'accessToken',
      'access_token',
      'refreshToken',
      'refresh_token',
      'idToken',
      'id_token',
      'password',
      'newPassword',
      'pin',
      'otp',
      'authorization',
      // identity
      'email',
      'username',
      // person
      'full_name',
      'fullname',
      'fullName',
      'name',
      'surnameV2',
      'alias',
      'dob',
      'dateOfBirth',
      'date_of_birth',
      'uin',
      'partialNric',
      'partial_nric',
      'personalId',
      'personal_id',
      'phone',
      'mobileNumber',
      'localMobileNumber',
      'contact_number',
    ].join('|') +
    ')"\\s*:\\s*("[^"]*"|\\d+|true|false|null)',
  'gi',
)

const REDACTED = '[REDACTED]'

export const scrubString = (value) => {
  if (typeof value !== 'string' || value.length === 0) return value
  return value
    .replace(JWT, REDACTED)
    .replace(AUTH_HEADER, `$1${REDACTED}`)
    .replace(SENSITIVE_PAIR, (_match, key) => `"${key}":"${REDACTED}"`)
}

// Everything after `?` or `#`. Paths are enough to debug with; the query is
// where form prefills and auth redirect codes live.
export const stripUrlQuery = (url) => {
  if (typeof url !== 'string') return url
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : url.slice(0, cut)
}

const isUrlKey = (key) => /url$|referrer$/i.test(key)

// Walks an event, stripping queries from URL fields and scrubbing every other
// string. Depth-limited and cycle-guarded: an error's shape is not ours.
const scrubEvent = (value, key = '', depth = 0, seen = new WeakSet()) => {
  if (depth > 8) return value
  if (typeof value === 'string') return isUrlKey(key) ? stripUrlQuery(value) : scrubString(value)
  if (value === null || typeof value !== 'object' || seen.has(value)) return value
  seen.add(value)
  for (const k of Object.keys(value)) {
    value[k] = scrubEvent(value[k], Array.isArray(value) ? key : k, depth + 1, seen)
  }
  return value
}

// `beforeSend` for datadogLogs.init. Never throws, never drops an event: on an
// unexpected failure the message is blanked rather than sent raw.
export const scrubLogsBeforeSend = (event) => {
  try {
    scrubEvent(event)
  } catch {
    if (event && typeof event === 'object') event.message = '[REDACTED: scrubber failed]'
  }
  return true
}

// `beforeSend` for datadogRum.init. Only the fields RUM lets beforeSend change:
// the URLs it records and an error's text.
export const scrubRumBeforeSend = (event) => {
  try {
    if (event && event.view) {
      event.view.url = stripUrlQuery(event.view.url)
      event.view.referrer = stripUrlQuery(event.view.referrer)
    }
    if (event && event.resource) {
      event.resource.url = stripUrlQuery(event.resource.url)
    }
    if (event && event.error) {
      if (event.error.resource) {
        event.error.resource.url = stripUrlQuery(event.error.resource.url)
      }
      event.error.message = scrubString(event.error.message)
      event.error.stack = scrubString(event.error.stack)
    }
  } catch {
    // A half-scrubbed event is still better than none reaching Datadog.
  }
  return true
}
